import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import _Icon from '../../../components/AppIcon';
import _Button from '../../../components/ui/Button';
import { supabase } from '../../../utils/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { processSkillsData, getSafeSkillName, getSafeSkillExperience } from '../../../utils/skillsDataParser';

const SkillsSection = ({ profile, isPublicView = false, skillAssessments: passedAssessments }) => {
  const isLoading = !profile;
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('rating'); // 'name', 'proficiency', 'rating', 'recent'
  const [assessmentData, setAssessmentData] = useState({
    userAssessments: [],
    skillCategories: [],
    skills: [],
    aiAssessments: new Map()
  });
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showAssessmentQA, setShowAssessmentQA] = useState(false);
  const [assessmentConversation, setAssessmentConversation] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);

  const handleTakeAssessment = useCallback(() => {
    navigate('/skills-assessment');
  }, [navigate]);

  const handleManageSkills = useCallback(() => {
    navigate('/va-profile-settings');
  }, [navigate]);

  const handleUpSkills = useCallback(() => {
    navigate('/up-skills');
  }, [navigate]);

  const handleSkillClick = useCallback((skill) => {
    if (skill.experience && skill.experience.trim()) {
      setSelectedSkill(skill);
      setShowSkillModal(true);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowSkillModal(false);
    setSelectedSkill(null);
    setShowAssessmentQA(false);
    setAssessmentConversation(null);
  }, []);

  const fetchAssessmentConversation = useCallback(async (sessionId) => {
    if (!sessionId) return;
    
    setLoadingConversation(true);
    try {
      const { data, error } = await supabase
        .from('ai_skill_assessment_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) throw error;
      
      console.log('[SkillsSection] Fetched assessment conversation:', {
        sessionId,
        data,
        hasConversationLog: !!data?.conversation_log,
        conversationLogLength: Array.isArray(data?.conversation_log) ? data.conversation_log.length : 'not array',
        hasEvaluation: !!data?.ai_evaluation
      });
      
      setAssessmentConversation(data);
    } catch (error) {
      console.error('Error fetching assessment conversation:', error);
    } finally {
      setLoadingConversation(false);
    }
  }, []);

  const toggleAssessmentQA = useCallback(async (sessionId) => {
    if (!showAssessmentQA && !assessmentConversation) {
      await fetchAssessmentConversation(sessionId);
    }
    setShowAssessmentQA(prev => !prev);
  }, [showAssessmentQA, assessmentConversation, fetchAssessmentConversation]);



  // Function to sync and clean skills data in the database
  const syncSkillsData = useCallback(async (cleanedSkills) => {
    if (!profile?.id || isPublicView) return;
    
    try {
      console.log('Syncing cleaned skills data to database:', cleanedSkills);
      
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          skills: cleanedSkills,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);
      
      if (error) {
        console.error('Error syncing skills data:', error);
      } else {
        console.log('Successfully synced cleaned skills data');
      }
    } catch (error) {
      console.error('Error in syncSkillsData:', error);
    }
  }, [profile?.id, isPublicView]);

  // Fetch skills assessment data when profile loads
  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!profile?.id) return;

      // If passed assessments are provided (from public profile), use them directly
      if (passedAssessments && passedAssessments.aiAssessments && passedAssessments.aiAssessments.length > 0) {
        // Convert array to Map for consistency
        const aiAssessmentMap = new Map();
        passedAssessments.aiAssessments.forEach(assessment => {
          if (!aiAssessmentMap.has(assessment.skill_name)) {
            aiAssessmentMap.set(assessment.skill_name, assessment);
          }
        });

        setAssessmentData({
          userAssessments: passedAssessments.userAssessments || [],
          skillCategories: passedAssessments.skillCategories || [],
          skills: passedAssessments.skills || [],
          aiAssessments: aiAssessmentMap
        });
        setIsLoadingAssessments(false);
        return;
      }

      setIsLoadingAssessments(true);
      try {
        // Fetch skill categories
        const { data: categories, error: categoriesError } = await supabase
          .from('skill_categories')
          .select('*')
          .order('name');

        // Fetch skills with categories
        const { data: skills, error: skillsError } = await supabase
          .from('skills')
          .select(`
            *,
            skill_categories(name, icon)
          `)
          .eq('active', true)
          .order('name');

        // Fetch user's skill assessments
        const { data: userAssessments, error: assessmentsError } = await supabase
          .from('user_skill_assessments')
          .select(`
            *,
            skills(
              id,
              name,
              difficulty_level,
              skill_categories(name, icon)
            )
          `)
          .eq('user_id', profile.id)
          .order('completed_at', { ascending: false });

        // Fetch user's AI skill assessments - get the latest passed assessment for each skill
        const { data: aiAssessments, error: aiAssessmentsError } = await supabase
          .from('ai_skill_assessment_sessions')
          .select('*')
          .eq('user_id', profile.id)
          .eq('passed', true)
          .order('updated_at', { ascending: false });

        if (categoriesError) throw categoriesError;
        if (skillsError) throw skillsError;
        if (assessmentsError) throw assessmentsError;
        if (aiAssessmentsError) throw aiAssessmentsError;

        // Create a map of AI assessments by skill name (latest passing assessment for each skill)
        const aiAssessmentMap = new Map();
        if (aiAssessments && aiAssessments.length > 0) {
          aiAssessments.forEach(assessment => {
            // Only store if we don't have an entry for this skill yet (ensuring we keep the most recent)
            if (!aiAssessmentMap.has(assessment.skill_name)) {
              aiAssessmentMap.set(assessment.skill_name, assessment);
            }
          });
        }

        setAssessmentData({
          userAssessments: userAssessments || [],
          skillCategories: categories || [],
          skills: skills || [],
          aiAssessments: aiAssessmentMap
        });
      } catch (error) {
        console.error('Error fetching assessment data:', error);
      } finally {
        setIsLoadingAssessments(false);
      }
    };

    fetchAssessmentData();
  }, [profile?.id, passedAssessments]);

  // Enhanced skill icon mapping with database integration
  const getSkillIcon = useCallback((category, categoryIcon) => {
    // Use icon from database if available
    if (categoryIcon) {
      const iconMap = {
        'clipboard': 'FileText',
        'message-circle': 'MessageCircle',
        'code': 'Code',
        'trello': 'Briefcase',
        'trending-up': 'TrendingUp',
        'calendar': 'Calendar',
        'mail': 'Mail',
        'database': 'Database',
        'globe': 'Globe',
        'image': 'Image',
        'pen-tool': 'PenTool',
        'headphones': 'Headphones',
        'calculator': 'Calculator',
        'search': 'Search',
        'bar-chart': 'BarChart',
        'dollar-sign': 'DollarSign',
        'figma': 'Figma',
        'edit': 'Edit',
        'share-2': 'Share2'
      };
      return iconMap[categoryIcon] || 'Award';
    }

    // Handle object categories
    let categoryStr = '';
    if (typeof category === 'string') {
      categoryStr = category;
    } else if (category && typeof category === 'object') {
      if (typeof category.name === 'string') {
        categoryStr = category.name;
      }
    }
    
    if (!categoryStr) return 'Award';
    
    // Fallback to category-based mapping
    const iconMap = {
      'administrative': 'FileText',
      'technical': 'Code',
      'creative': 'PenTool',
      'marketing': 'TrendingUp',
      'communication': 'MessageCircle',
      'management': 'Briefcase',
      'project management': 'Briefcase',
      'data': 'BarChart',
      'customer_service': 'Headphones',
      'finance': 'DollarSign',
      'design': 'Figma',
      'writing': 'Edit',
      'research': 'Search',
      'project_management': 'Briefcase',
      'data_analysis': 'BarChart'
    };
    return iconMap[categoryStr.toLowerCase().replace(/\s+/g, '_')] || 'Award';
  }, []);

  // Enhanced level color mapping
  const getLevelColor = useCallback((level) => {
    if (!level || typeof level !== 'string') return 'text-gray-600 bg-gray-50 border-gray-200';
    
    switch (level.toLowerCase()) {
      case 'expert':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'advanced':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'intermediate':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'beginner':
        return 'text-gray-700 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  }, []);

  // Helper function to categorize legacy string skills
  const getCategoryForSkill = useCallback((skillName) => {
    if (!skillName || typeof skillName !== 'string') return 'Other';
    
    const skill = skillName.toLowerCase();
    
    // Try to find matching skill in database first
    const matchingSkill = assessmentData.skills.find(dbSkill => 
      dbSkill.name.toLowerCase() === skill
    );
    
    if (matchingSkill) {
      return matchingSkill.skill_categories?.name || 'Other';
    }
    
    // Fallback to keyword matching
    const categoryKeywords = {
      'Administrative': ['admin', 'data entry', 'filing', 'scheduling', 'calendar', 'email', 'office', 'clerical', 'typing', 'assistant', 'virtual assistant'],
      'Communication': ['communication', 'phone', 'email', 'chat', 'correspondence', 'interpersonal', 'presentation', 'meeting', 'negotiation'],
      'Technical': ['technical', 'software', 'computer', 'programming', 'coding', 'web', 'database', 'system', 'it', 'technology'],
      'Project Management': ['project', 'management', 'planning', 'coordination', 'organization', 'timeline', 'resource', 'team lead'],
      'Marketing': ['marketing', 'social media', 'seo', 'content', 'advertising', 'promotion', 'brand', 'campaign', 'digital marketing']
    };
    
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some(keyword => skill.includes(keyword))) {
        return category;
      }
    }
    
    return 'Other';
  }, [assessmentData.skills]);



  // Process skills with assessment integration using enhanced parser
  const processedSkills = useMemo(() => {
    if (isLoading || isLoadingAssessments) return [];
    
    const rawSkills = profile?.skills || [];
    
    // Use the enhanced skills parser
    const { skills: normalizedSkills, stats, hasRawJsonIssues, needsSync } = processSkillsData(rawSkills);
    
    // If we found and fixed raw JSON issues, sync the cleaned data back to the database
    if (needsSync) {
      console.log(`SkillsSection: Fixed raw JSON issues. Stats:`, stats);
      // Use a timeout to avoid blocking the UI
      setTimeout(() => syncSkillsData(normalizedSkills), 1000);
    }
    
    // Integrate with assessment data and add additional fields
    return normalizedSkills.map((skill, index) => {
      // Skip invalid skills
      if (!skill || (typeof skill === 'object' && Object.keys(skill).length === 0)) {
        return null;
      }
      
      // Ensure skill is an object
      const skillObj = typeof skill === 'string' ? { name: skill, type: 'legacy' } : skill;
      
      // Find matching assessment data
      const skillName = getSafeSkillName(skillObj);
      const assessmentResult = assessmentData.userAssessments.find(assessment => 
        assessment.skills?.name === skillName
      );
      
      // Find matching AI assessment data from the database
      const aiAssessmentFromDb = assessmentData.aiAssessments?.get(skillName);
      
      // Ensure category is a string
      let category = 'Other';
      if (typeof skillObj.category === 'string') {
        category = skillObj.category;
      } else if (skillObj.category && typeof skillObj.category === 'object') {
        if (typeof skillObj.category.name === 'string') {
          category = skillObj.category.name;
        }
      }

      // Determine source: AI Assessment from DB > AI Assessment from profile > Assessment Table > Self-Rated > Manual
      const aiAssessmentData = aiAssessmentFromDb || {
        ai_assessed: skillObj.ai_assessed,
        ai_assessment_score: skillObj.ai_assessment_score,
        ai_proficiency_level: skillObj.ai_proficiency_level,
        ai_assessment_session_id: skillObj.ai_assessment_session_id,
        ai_assessment_date: skillObj.ai_assessment_date
      };
      
      const hasAIAssessment = Boolean(aiAssessmentData?.ai_assessed || aiAssessmentFromDb);
      const hasValidAIScore = aiAssessmentData?.final_score !== null && aiAssessmentData?.final_score !== undefined;
      const hasLegacyAssessment = !!assessmentResult;
      const isSelfRated = skillObj.type === 'upskills';
      
      // Calculate verified status
      const aiAssessmentScore = aiAssessmentData?.final_score || aiAssessmentData?.ai_assessment_score;
      const aiAssessmentVerified = hasAIAssessment && aiAssessmentScore && Number(aiAssessmentScore) >= 70;
      const isVerified = assessmentResult?.passed || skillObj.verified || aiAssessmentVerified;
      
      // Log AI assessment details for debugging
      if ((aiAssessmentData?.ai_assessed || aiAssessmentScore) && (aiAssessmentFromDb || skillObj.ai_assessment_score)) {
        console.log(`[SkillsSection] AI Assessment Debug - Skill: ${skillName}`, {
          ai_assessed: aiAssessmentData?.ai_assessed,
          ai_assessment_score: aiAssessmentScore,
          ai_assessment_score_type: typeof aiAssessmentScore,
          ai_proficiency_level: aiAssessmentData?.proficiency_level || aiAssessmentData?.ai_proficiency_level,
          aiAssessmentVerified,
          isVerified,
          hasAIAssessment,
          fromDatabase: !!aiAssessmentFromDb,
          aiAssessmentData
        });
      }
      
      return {
        ...skillObj,
        // Ensure name is properly set
        name: skillName,
        // Override with assessment data if available
        category: assessmentResult?.skills?.skill_categories?.name || category || getCategoryForSkill(skillName),
        level: assessmentResult?.skills?.difficulty_level || skillObj.level || 'Intermediate',
        proficiency: assessmentResult ? Math.round(assessmentResult.percentage) : 
                    (aiAssessmentScore || (skillObj.rating ? (skillObj.rating * 20) : 75)),
        verified: isVerified,
        assessmentScore: assessmentResult?.score || aiAssessmentScore || null,
        maxScore: assessmentResult?.max_score || 100,
        completedAt: assessmentResult?.completed_at || aiAssessmentData?.updated_at || skillObj.ai_assessment_date || null,
        // Source detection: AI Assessment takes priority
        databaseSkill: hasAIAssessment || hasLegacyAssessment,
        upSkillsFormat: isSelfRated,
        // AI Assessment metadata - use data from database if available, fallback to profile data
        ai_assessed: aiAssessmentFromDb ? true : (skillObj.ai_assessed || false),
        ai_proficiency_level: aiAssessmentData?.proficiency_level || aiAssessmentData?.ai_proficiency_level,
        ai_assessment_score: aiAssessmentData?.final_score || skillObj.ai_assessment_score,
        ai_assessment_session_id: aiAssessmentFromDb?.id || skillObj.ai_assessment_session_id,
        ai_assessment_date: aiAssessmentData?.updated_at || skillObj.ai_assessment_date
      };
    }).filter(Boolean); // Remove any null entries
  }, [profile?.skills, assessmentData.userAssessments, assessmentData.aiAssessments, getCategoryForSkill, isLoading, isLoadingAssessments, syncSkillsData]);

  // Get available categories with counts
  const categoriesWithCounts = useMemo(() => {
    const categoryMap = new Map();
    
    // Add database categories first
    assessmentData.skillCategories.forEach(category => {
      categoryMap.set(category.name, { ...category, count: 0 });
    });
    
    // Count skills per category
    processedSkills.forEach(skill => {
      const categoryName = skill.category;
      if (categoryMap.has(categoryName)) {
        const category = categoryMap.get(categoryName);
        category.count++;
      } else {
        categoryMap.set(categoryName, { 
          id: `category-${categoryName}`, 
          name: categoryName, 
          count: 1, 
          icon: null 
        });
      }
    });
    
    // Convert to array and sort
    const categories = Array.from(categoryMap.values())
      .filter(category => category.count > 0)
      .sort((a, b) => b.count - a.count);
    
    return [{ id: 'all', name: 'All', count: processedSkills.length, icon: null }, ...categories];
  }, [processedSkills, assessmentData.skillCategories]);

  // Filter and sort skills based on selected category and sort option
  const filteredSkills = useMemo(() => {
    // First filter by category
    let filtered = selectedCategory === 'All' 
      ? processedSkills 
      : processedSkills.filter(skill => skill.category === selectedCategory);

    // Validate filtered skills - ensure all skills have proper structure
    filtered = filtered.filter(skill => {
      if (!skill || typeof skill !== 'object') {
        console.warn('Invalid skill object found:', skill);
        return false;
      }
      if (typeof skill.name !== 'string' || !skill.name.trim()) {
        console.warn('Skill with invalid or empty name found:', skill);
        return false;
      }
      return true;
    });

    // Then sort based on selected option
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'proficiency':
          return b.proficiency - a.proficiency; // High to low
        case 'rating': {
          // Sort by rating (high to low), then by proficiency for non-rated skills
          if (a.rating && b.rating) {
            return b.rating - a.rating;
          } else if (a.rating && !b.rating) {
            return -1; // Rated skills first
          } else if (!a.rating && b.rating) {
            return 1; // Rated skills first
          } else {
            return b.proficiency - a.proficiency; // Fallback to proficiency
          }
        }
        case 'recent': {
          // Sort by assessment completion date, then by creation/update time
          const aDate = a.completedAt ? new Date(a.completedAt) : new Date(0);
          const bDate = b.completedAt ? new Date(b.completedAt) : new Date(0);
          return bDate - aDate; // Most recent first
        }
        case 'verified': {
          // Verified skills first, then by proficiency
          if (a.verified && !b.verified) return -1;
          if (!a.verified && b.verified) return 1;
          return b.proficiency - a.proficiency;
        }
        case 'name':
        default:
          return a.name.localeCompare(b.name); // Alphabetical
      }
    });

    return sorted;
  }, [processedSkills, selectedCategory, sortBy]);

  // Statistics for verified vs unverified skills and up-skills format
  const skillStats = useMemo(() => {
    const verified = processedSkills.filter(skill => skill.verified).length;
    const upSkillsFormat = processedSkills.filter(skill => skill.upSkillsFormat).length;
    const withRating = processedSkills.filter(skill => skill.rating && skill.rating > 0).length;
    const withExperience = processedSkills.filter(skill => skill.experience && skill.experience.trim()).length;
    const recoveredFromRawJson = processedSkills.filter(skill => skill.wasRawJson).length;
    const total = processedSkills.length;
    const unverified = total - verified;
    
    return { 
      verified, 
      unverified, 
      total, 
      upSkillsFormat, 
      withRating, 
      withExperience, 
      recoveredFromRawJson 
    };
  }, [processedSkills]);

  // Handle category selection
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  // Handle sorting selection
  const handleSortSelect = useCallback((sortOption) => {
    setSortBy(sortOption);
  }, []);

  // Loading state
  if (isLoading || isLoadingAssessments) {
    return (
      <div className="content-container bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!processedSkills || processedSkills.length === 0) {
    return (
      <div className="content-container bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <_Icon name="Award" size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Skills Listed</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Add your skills and take assessments to showcase your expertise to potential employers.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {!isPublicView && (
              <_Button 
                onClick={handleUpSkills}
                variant="primary" 
                size="md"
                className="flex items-center gap-2"
              >
                <_Icon name="TrendingUp" size={16} />
                Upskill Profile
              </_Button>
            )}
            {/* <_Button 
              onClick={handleManageSkills}
              variant="outline" 
              size="md"
              className="flex items-center gap-2"
            >
              <_Icon name="Plus" size={16} />
              Add Skills
            </_Button> */}
            {!isPublicView && (
              <_Button 
                onClick={handleTakeAssessment}
                variant="outline" 
                size="md"
                className="flex items-center gap-2"
              >
                <_Icon name="FileText" size={16} />
                Take Assessment
              </_Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Skill Detail Modal Component
  const _SkillDetailModal = () => {
    if (!showSkillModal || !selectedSkill) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleCloseModal}
      >
        <div 
          className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-200">
                <_Icon 
                  name={getSkillIcon(selectedSkill.category, assessmentData.skillCategories.find(c => c.name === selectedSkill.category)?.icon)} 
                  size={20} 
                  className="text-blue-600" 
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {getSafeSkillName(selectedSkill)}
                </h2>
                <p className="text-sm text-gray-600">
                  {typeof selectedSkill.category === 'string' ? selectedSkill.category : 'Other'}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseModal}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <_Icon name="X" size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6 space-y-6">
            {/* Skill Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Proficiency & Rating */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Proficiency Level</h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Overall Score</span>
                    <span className="text-sm font-medium text-gray-900">{selectedSkill.proficiency}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${selectedSkill.proficiency}%` }}
                    ></div>
                  </div>
                </div>

                {selectedSkill.rating && selectedSkill.upSkillsFormat && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Self Rating</h3>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <_Icon
                            key={star}
                            name="Star"
                            size={20}
                            className={`${
                              star <= selectedSkill.rating 
                                ? 'text-yellow-400 fill-current' 
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">({selectedSkill.rating}/5)</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Status & Verification */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Status</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Level</span>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getLevelColor(selectedSkill.level)}`}>
                        {selectedSkill.level}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Verification</span>
                      <div className="flex items-center gap-1">
                        {(() => {
                          // Normalize boolean check - handle both boolean and string types
                          const isVerified = selectedSkill.verified === true || selectedSkill.verified === 'true';
                          return isVerified ? (
                            <>
                              <_Icon name="CheckCircle" size={16} className="text-green-500" />
                              <span className="text-sm text-green-600 font-medium">Verified</span>
                            </>
                          ) : (
                            <>
                              <_Icon name="Clock" size={16} className="text-gray-400" />
                              <span className="text-sm text-gray-600">Unverified</span>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Source</span>
                      <div className="flex items-center gap-1">
                        {(() => {
                          const isAIAssessed = selectedSkill.ai_assessed === true || selectedSkill.ai_assessed === 'true';
                          const hasAIScore = selectedSkill.ai_assessment_score !== null && selectedSkill.ai_assessment_score !== undefined;
                          
                          if (isAIAssessed && hasAIScore) {
                            return (
                              <>
                                <span className="text-sm text-green-700 font-semibold">AI Verified</span>
                              </>
                            );
                          } else if (selectedSkill.databaseSkill && !selectedSkill.upSkillsFormat) {
                            return (
                              <>
                                <_Icon name="Database" size={16} className="text-blue-500" />
                                <span className="text-sm text-blue-600">Assessment</span>
                              </>
                            );
                          } else if (selectedSkill.upSkillsFormat) {
                            return (
                              <>
                                <_Icon name="Star" size={16} className="text-yellow-500" />
                                <span className="text-sm text-yellow-600">Self-Rated</span>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <_Icon name="User" size={16} className="text-gray-500" />
                                <span className="text-sm text-gray-600">Manual</span>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Experience Description */}
            {selectedSkill.experience && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Experience & Background</h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {getSafeSkillExperience(selectedSkill)}
                  </p>
                </div>
              </div>
            )}

            {/* AI Assessment Information */}
            {(() => {
              const isAIAssessed = selectedSkill.ai_assessed === true || selectedSkill.ai_assessed === 'true';
              const hasAIScore = selectedSkill.ai_assessment_score !== null && selectedSkill.ai_assessment_score !== undefined;
              return isAIAssessed && hasAIScore && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    AI Skills Assessment Results
                  </h3>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">Assessment Score</span>
                      <span className="text-lg font-semibold text-green-900">
                        {selectedSkill.ai_assessment_score}/100
                      </span>
                    </div>
                  {selectedSkill.ai_proficiency_level && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">Proficiency Level</span>
                      <span className="text-sm font-semibold text-green-900">
                        {selectedSkill.ai_proficiency_level.charAt(0).toUpperCase() + selectedSkill.ai_proficiency_level.slice(1)}
                      </span>
                    </div>
                  )}
                  {selectedSkill.ai_assessment_date && (
                    <p className="text-xs text-green-600 pt-2 border-t border-green-200">
                      Assessed on {new Date(selectedSkill.ai_assessment_date).toLocaleDateString()}
                    </p>
                  )}
                  
                  {/* View Assessment Q&A Button - For own profile or employers viewing public profiles */}
                  {(!isPublicView || userProfile?.role === 'employer') && selectedSkill.ai_assessment_session_id && (
                    <div className="pt-3 border-t border-green-200">
                      <_Button
                        onClick={() => toggleAssessmentQA(selectedSkill.ai_assessment_session_id)}
                        variant="outline"
                        size="sm"
                        className="w-full flex items-center justify-center gap-2"
                        disabled={loadingConversation}
                      >
                        <_Icon name={showAssessmentQA ? "ChevronUp" : "ChevronDown"} size={16} />
                        {loadingConversation ? 'Loading...' : (showAssessmentQA ? 'Hide Assessment Details' : 'View Questions & Answers')}
                      </_Button>
                    </div>
                  )}
                  
                  {/* Assessment Conversation Display - For own profile or employers viewing public profiles */}
                  {(!isPublicView || userProfile?.role === 'employer') && showAssessmentQA && assessmentConversation && (
                    <div className="pt-3 border-t border-green-200 space-y-4">
                      {(() => {
                        console.log('[SkillsSection Modal] Assessment Conversation State:', {
                          showAssessmentQA,
                          hasConversationData: !!assessmentConversation,
                          hasEvaluation: !!assessmentConversation?.ai_evaluation,
                          hasConversationLog: !!assessmentConversation?.conversation_log,
                          conversationLogType: typeof assessmentConversation?.conversation_log,
                          conversationLogIsArray: Array.isArray(assessmentConversation?.conversation_log),
                          conversationLogLength: Array.isArray(assessmentConversation?.conversation_log) ? assessmentConversation.conversation_log.length : 'not array'
                        });
                        
                        // Log the structure of the first entry
                        if (Array.isArray(assessmentConversation?.conversation_log) && assessmentConversation.conversation_log.length > 0) {
                          console.log('[SkillsSection Modal] First conversation entry structure:', {
                            firstEntry: assessmentConversation.conversation_log[0],
                            firstEntryKeys: Object.keys(assessmentConversation.conversation_log[0]),
                            hasQuestion: 'question' in assessmentConversation.conversation_log[0],
                            hasUserResponse: 'user_response' in assessmentConversation.conversation_log[0],
                            hasFeedback: 'feedback' in assessmentConversation.conversation_log[0],
                            allEntries: assessmentConversation.conversation_log
                          });
                        }
                        return null;
                      })()}
                      {/* AI Evaluation Summary */}
                      {assessmentConversation.ai_evaluation && (
                        <div className="bg-white rounded-lg p-3 border border-green-300">
                          <h4 className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1">
                            <_Icon name="CheckCircle" size={14} className="text-green-600" />
                            AI Evaluation Summary
                          </h4>
                          <div className="space-y-2 text-xs text-gray-700">
                            {assessmentConversation.ai_evaluation.feedback && (
                              <div>
                                <span className="font-medium text-gray-900">Overall Feedback:</span>
                                <p className="mt-1">{assessmentConversation.ai_evaluation.feedback}</p>
                              </div>
                            )}
                            {assessmentConversation.ai_evaluation.strengths && assessmentConversation.ai_evaluation.strengths.length > 0 && (
                              <div>
                                <span className="font-medium text-gray-900">Strengths:</span>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                  {assessmentConversation.ai_evaluation.strengths.map((strength, idx) => (
                                    <li key={idx}>{strength}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {assessmentConversation.ai_evaluation.areas_for_improvement && assessmentConversation.ai_evaluation.areas_for_improvement.length > 0 && (
                              <div>
                                <span className="font-medium text-gray-900">Areas for Improvement:</span>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                  {assessmentConversation.ai_evaluation.areas_for_improvement.map((area, idx) => (
                                    <li key={idx}>{area}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Conversation Log */}
                      {assessmentConversation.conversation_log && assessmentConversation.conversation_log.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-green-800 mb-3 flex items-center gap-1">
                            <_Icon name="MessageCircle" size={14} className="text-green-600" />
                            Assessment Conversation ({assessmentConversation.conversation_log.length} rounds)
                          </h4>
                          <div className="space-y-3">
                            {assessmentConversation.conversation_log.map((entry, index) => (
                              <div key={index} className="bg-white rounded-lg p-3 border border-green-200">
                                <div className="mb-2 flex items-center justify-between">
                                  <span className="text-xs font-semibold text-green-800">Round {entry.round || index + 1}</span>
                                  {entry.difficulty && (
                                    <span className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full border border-green-200">
                                      {entry.difficulty.charAt(0).toUpperCase() + entry.difficulty.slice(1)} Difficulty
                                    </span>
                                  )}
                                </div>
                                
                                {/* Question */}
                                <div className="mb-3">
                                  <div className="flex items-start gap-2">
                                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-xs">🤖</span>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-gray-900 mb-1">Question:</p>
                                      <p className="text-xs text-gray-700 leading-relaxed">{entry.content}</p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Answer */}
                                <div>
                                  <div className="flex items-start gap-2">
                                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      <_Icon name="User" size={12} className="text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-gray-900 mb-1">Your Answer:</p>
                                      <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{entry.user_answer}</p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* AI Evaluation if available */}
                                {entry.ai_evaluation && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-start gap-2">
                                      <_Icon name="Info" size={14} className="text-blue-500 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-xs font-medium text-gray-900 mb-1">AI Evaluation:</p>
                                        {typeof entry.ai_evaluation === 'string' ? (
                                          <p className="text-xs text-gray-600 leading-relaxed">{entry.ai_evaluation}</p>
                                        ) : (
                                          <div className="space-y-1">
                                            {entry.ai_evaluation.comments && (
                                              <p className="text-xs text-gray-600 leading-relaxed">{entry.ai_evaluation.comments}</p>
                                            )}
                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                              {entry.ai_evaluation.score !== undefined && (
                                                <div className="text-xs">
                                                  <span className="font-medium text-gray-700">Score:</span>
                                                  <span className="ml-1 text-gray-600">{Math.round(entry.ai_evaluation.score)}/100</span>
                                                </div>
                                              )}
                                              {entry.ai_evaluation.clarity !== undefined && (
                                                <div className="text-xs">
                                                  <span className="font-medium text-gray-700">Clarity:</span>
                                                  <span className="ml-1 text-gray-600">{Math.round(entry.ai_evaluation.clarity)}/10</span>
                                                </div>
                                              )}
                                              {entry.ai_evaluation.depth !== undefined && (
                                                <div className="text-xs">
                                                  <span className="font-medium text-gray-700">Depth:</span>
                                                  <span className="ml-1 text-gray-600">{Math.round(entry.ai_evaluation.depth)}/10</span>
                                                </div>
                                              )}
                                              {entry.ai_evaluation.technical_accuracy !== undefined && (
                                                <div className="text-xs">
                                                  <span className="font-medium text-gray-700">Accuracy:</span>
                                                  <span className="ml-1 text-gray-600">{Math.round(entry.ai_evaluation.technical_accuracy)}/10</span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Expected Topics if available */}
                                {entry.expectedTopics && entry.expectedTopics.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-start gap-2">
                                      <_Icon name="Target" size={14} className="text-amber-500 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-xs font-medium text-gray-900 mb-1">Expected Topics:</p>
                                        <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                                          {entry.expectedTopics.map((topic, idx) => (
                                            <li key={idx}>{topic}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })()}

            {/* Legacy Assessment Information */}
            {(() => {
              const isAIAssessed = selectedSkill.ai_assessed === true || selectedSkill.ai_assessed === 'true';
              const hasAIScore = selectedSkill.ai_assessment_score !== null && selectedSkill.ai_assessment_score !== undefined;
              return selectedSkill.assessmentScore && !isAIAssessed && !hasAIScore && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Assessment Results</h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-blue-700">Last Assessment Score</span>
                      <span className="text-lg font-semibold text-blue-900">
                        {selectedSkill.assessmentScore}/{selectedSkill.maxScore || 100}
                      </span>
                  </div>
                  {selectedSkill.completedAt && (
                    <p className="text-xs text-blue-600">
                      Completed on {new Date(selectedSkill.completedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              );
            })()}

            {/* Years of Experience */}
            {selectedSkill.yearsOfExperience > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Experience Duration</h3>
                <div className="flex items-center gap-2">
                  <_Icon name="Calendar" size={16} className="text-gray-500" />
                  <span className="text-sm text-gray-700">
                    {selectedSkill.yearsOfExperience} {selectedSkill.yearsOfExperience === 1 ? 'year' : 'years'} of experience
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <_Button
              onClick={handleCloseModal}
              variant="outline"
              size="sm"
            >
              Close
            </_Button>
            {!isPublicView && (
              <_Button
                onClick={() => {
                  handleCloseModal();
                  navigate('/up-skills');
                }}
                variant="primary"
                size="sm"
                className="flex items-center gap-2"
              >
                <_Icon name="Edit" size={16} />
                Edit Skill
              </_Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <_SkillDetailModal />
      <section className="content-container bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <_Icon name="Award" size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Skills & Expertise</h2>
              <p className="text-sm text-gray-600">
                {skillStats.total} skills • {/* {skillStats.verified} verified • */} {skillStats.withRating} rated • {skillStats.withExperience} detailed
                {skillStats.recoveredFromRawJson > 0 && (
                  <span className="text-green-600 font-medium"> • {skillStats.recoveredFromRawJson} recovered</span>
                )}
              </p>
            </div>
          </div>
          
          {!isPublicView && (
            <_Button
              onClick={handleUpSkills}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <_Icon name="TrendingUp" size={16} />
              Upskill Profile
            </_Button>
          )}
        </div>

        {/* Category Tabs */}
        <div className="flex flex-col gap-4">
          {/* Category Filters */}
          <div className="flex flex-col gap-4 md:flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <_Icon name="Filter" size={16} className="text-gray-500 mr-2" />
              <div className="flex flex-wrap gap-2">
                {categoriesWithCounts.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.name)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                      selectedCategory === category.name
                        ? 'bg-blue-100 text-blue-700 border-blue-200'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {category.name}
                    <span className={`ml-1.5 text-xs ${
                      selectedCategory === category.name ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Skills Stats */}
            {/* <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>{skillStats.verified} Verified</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span>{skillStats.unverified} Unverified</span>
              </div>
            </div> */}
          </div>

          {/* Sort Options */}
          <div className="flex flex-col gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <_Icon name="ArrowUpDown" size={16} className="text-gray-500 mr-2" />
              <span className="text-sm font-medium text-gray-700 mr-2">Sort by:</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'name', label: 'Name', icon: 'Type' },
                  { value: 'proficiency', label: 'Proficiency', icon: 'TrendingUp' },
                  { value: 'rating', label: 'Star Rating', icon: 'Star' },
                  { value: 'verified', label: 'Verified', icon: 'CheckCircle' },
                  { value: 'recent', label: 'Recent', icon: 'Clock' }
                ].map((sort) => (
                  <button
                    key={sort.value}
                    onClick={() => handleSortSelect(sort.value)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors flex items-center gap-1.5 ${
                      sortBy === sort.value
                        ? 'bg-green-100 text-green-700 border-green-200'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <_Icon 
                      name={sort.icon} 
                      size={12} 
                      className={sortBy === sort.value ? 'text-green-600' : 'text-gray-500'} 
                    />
                    {sort.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Active filters indicator */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Showing {filteredSkills.length} of {processedSkills.length} skills</span>
              {sortBy !== 'name' && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                  Sorted by {sortBy === 'proficiency' ? 'highest proficiency' : 
                           sortBy === 'rating' ? 'highest rating' : 
                           sortBy === 'verified' ? 'verified first' : 
                           sortBy === 'recent' ? 'most recent' : sortBy}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Skills Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSkills.map((skill) => (
            <div
              key={skill.id}
              className={`bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors ${
                skill.experience && skill.experience.trim() 
                  ? 'cursor-pointer hover:bg-gray-100 hover:shadow-sm' 
                  : ''
              }`}
              onClick={() => handleSkillClick(skill)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                    <_Icon 
                      name={getSkillIcon(skill.category, assessmentData.skillCategories.find(c => c.name === skill.category)?.icon)} 
                      size={16} 
                      className="text-gray-600" 
                    />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">
                      {typeof skill.name === 'string' ? skill.name : getSafeSkillName(skill)}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {typeof skill.category === 'string' ? skill.category : 
                       skill.category && typeof skill.category === 'object' && typeof skill.category.name === 'string' ? skill.category.name : 'Other'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* AI Verified Badge - Passed Assessment */}
                  {(() => {
                    // Normalize boolean check - handle both boolean true and string "true"
                    const isAIAssessed = skill.ai_assessed === true || skill.ai_assessed === 'true';
                    const hasValidScore = skill.ai_assessment_score !== null && skill.ai_assessment_score !== undefined;
                    const isPassing = hasValidScore && Number(skill.ai_assessment_score) >= 70;
                    const shouldShow = isAIAssessed && hasValidScore && isPassing;
                    
                    if (isAIAssessed && hasValidScore) {
                      console.log(`[SkillsSection] AI Badge Check for: ${skill.name}`, {
                        ai_assessed: skill.ai_assessed,
                        ai_assessed_type: typeof skill.ai_assessed,
                        isAIAssessed,
                        verified: skill.verified,
                        verified_type: typeof skill.verified,
                        score: skill.ai_assessment_score,
                        score_type: typeof skill.ai_assessment_score,
                        isPassing,
                        shouldShow,
                        proficiency: skill.ai_proficiency_level
                      });
                    }
                    
                    return shouldShow && (
                      <div className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded-full border border-green-200">
                        
                        {skill.ai_proficiency_level && (
                          <span className="text-xs text-green-600">
                            ({skill.ai_proficiency_level.charAt(0).toUpperCase() + skill.ai_proficiency_level.slice(1)})
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {/* AI Assessment Pending/Failed - Retake Available */}
                  {(() => {
                    const isAIAssessed = skill.ai_assessed === true || skill.ai_assessed === 'true';
                    const hasScore = skill.ai_assessment_score !== null && skill.ai_assessment_score !== undefined;
                    const isFailed = hasScore && Number(skill.ai_assessment_score) < 70;
                    return isAIAssessed && isFailed && (
                      <div className="flex items-center gap-1 bg-amber-100 px-2 py-1 rounded-full border border-amber-200">
                        <span className="text-xs">⚠️</span>
                        <span className="text-xs text-amber-700 font-semibold">Retake Available</span>
                      </div>
                    );
                  })()}

                  {/* Regular Verified Badge - No AI Assessment */}
                  {(() => {
                    const isAIAssessed = skill.ai_assessed === true || skill.ai_assessed === 'true';
                    const hasAIScore = skill.ai_assessment_score !== null && skill.ai_assessment_score !== undefined;
                    const isRegularVerified = skill.verified && !isAIAssessed && !hasAIScore;
                    return isRegularVerified && (
                      <div className="flex items-center gap-1">
                        <_Icon name="CheckCircle" size={16} className="text-green-500" />
                        <span className="text-xs text-green-600 font-medium">Verified</span>
                      </div>
                    );
                  })()}
                  
                  {skill.experience && skill.experience.trim() && (
                    <div className="flex items-center gap-1">
                      <_Icon name="Info" size={16} className="text-blue-500" />
                    </div>
                  )}
                </div>
              </div>

              {/* Skill Level */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Proficiency</span>
                  <span className="text-xs font-medium text-gray-700">{skill.proficiency}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${skill.proficiency}%` }}
                  ></div>
                </div>
              </div>

              {/* Star Rating (from up-skills page) */}
              {skill.rating && skill.upSkillsFormat && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Self Rating</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <_Icon
                          key={star}
                          name="Star"
                          size={12}
                          className={`${
                            star <= skill.rating 
                              ? 'text-yellow-400 fill-current' 
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="text-xs text-gray-600 ml-1">({skill.rating}/5)</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Experience Description (from up-skills page) */}
              {skill.experience && skill.upSkillsFormat && (
                <div className="mb-3">
                  <div className="text-xs text-gray-600 mb-1">Experience</div>
                  <p className="text-xs text-gray-700 leading-relaxed" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {getSafeSkillExperience(skill)}
                  </p>
                </div>
              )}

              {/* Level Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getLevelColor(skill.level)}`}>
                  {skill.level}
                </span>

                {/* Source Badge - Priority: AI Assessment > Database Assessment > Self-Rated > Manual */}
                {(() => {
                  const isAIAssessed = skill.ai_assessed === true || skill.ai_assessed === 'true';
                  const hasAIScore = skill.ai_assessment_score !== null && skill.ai_assessment_score !== undefined;
                  
                  if (isAIAssessed && hasAIScore) {
                    return (
                      <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                        <span className="text-xs text-green-700 font-semibold">Verified</span>
                      </div>
                    );
                  } else if (skill.databaseSkill && !skill.upSkillsFormat) {
                    return (
                      <div className="flex items-center gap-1">
                        <_Icon name="Database" size={12} className="text-blue-500" />
                        <span className="text-xs text-blue-600">Assessment</span>
                      </div>
                    );
                  } else if (skill.upSkillsFormat) {
                    return (
                      <div className="flex items-center gap-1">
                        <_Icon name="Star" size={12} className="text-yellow-500" />
                        <span className="text-xs text-yellow-600">Self-Rated</span>
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex items-center gap-1">
                        <_Icon name="User" size={12} className="text-gray-500" />
                        <span className="text-xs text-gray-600">Manual</span>
                      </div>
                    );
                  }
                })()}

                {skill.wasRawJson && (
                  <div className="flex items-center gap-1">
                    <_Icon name="RefreshCw" size={12} className="text-green-500" />
                    <span className="text-xs text-green-600">Recovered</span>
                  </div>
                )}
              </div>

              {/* Assessment Info */}
              {skill.assessmentScore && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>Last Assessment:</span>
                    <span>{skill.assessmentScore}/{skill.maxScore || 100}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {!isPublicView && (
              <_Button
                onClick={handleUpSkills}
                variant="primary"
                size="md"
                className="flex items-center gap-2"
              >
                <_Icon name="TrendingUp" size={16} />
                Upskill Profile
              </_Button>
            )}
            {!isPublicView && (
              <_Button
                onClick={handleManageSkills}
                variant="outline"
                size="md"
                className="flex items-center gap-2"
              >
                <_Icon name="Settings" size={16} />
                Manage Skills
              </_Button>
            )}
            {/* <_Button
              onClick={handleTakeAssessment}
              variant="outline"
              size="md"
              className="flex items-center gap-2"
            >
              <_Icon name="FileText" size={16} />
              Take Assessment
            </_Button> */}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default SkillsSection;
